import { isTerminalStatus } from '../conversations/format';

import type { InboxTab } from './types';
import type { ConversationListItem } from '../../lib/types';
import type { ConversationsFilters } from '@lume/shared';

/**
 * Predicado das abas da Inbox (Brief 2B T1 §6.12) — filtro client-side de
 * exibição da lista, espelhando a semântica do agregado do servidor
 * (`GET /v1/conversations/counts`): badge e lista nunca divergem.
 *
 * Semântica honesta ao rótulo: "Aguardando minha ação" / "Aguardando
 * atendente" / "IA dirigindo" excluem conversas em status terminal
 * (encerrada/resolvida/arquivada não está aguardando ação de ninguém).
 * "Todas" mantém tudo.
 *
 * `currentUserId` vem do auth store — o literal `'me'` só é resolvido pelo
 * Engine; client-side comparamos com o id real. Null (auth ainda não
 * hidratado) não conta nada, defensivo.
 */
export function matchesTab(
  conversation: ConversationListItem,
  tab: InboxTab,
  currentUserId: string | null,
  opts?: { includeTerminal?: boolean },
): boolean {
  if (tab === 'all') return true;
  if (opts?.includeTerminal !== true && isTerminalStatus(conversation.status)) return false;
  switch (tab) {
    case 'awaiting-me':
      return currentUserId !== null && conversation.assignedToUserId === currentUserId;
    case 'awaiting-anyone':
      return conversation.assignedToUserId === null;
    case 'team-queue':
      // Item 366 Fase 4 — o pool do time já vem filtrado pelo servidor
      // (teamQueue=mine → assignedTeamId ∈ meus times, não atribuído). Aqui só
      // confirmamos o invariante "não atribuído"; sem dupla-filtragem client-side.
      return conversation.assignedToUserId === null;
    case 'ai-driving':
      return conversation.operationMode.current === 'H_c';
  }
}

/**
 * Override de filtros por aba — a QUERY enviada ao servidor (distinto de
 * `matchesTab`, que é o filtro de EXIBIÇÃO client-side sobre o resultado já
 * chegado). `InboxPage` monta `base` (filtros do usuário sem override de
 * aba) e chama esta função para a query da aba ativa.
 *
 * Rodada 3 Task 4 — a aba "Todas" manda `status=any` (sentinel: nenhuma
 * cláusula de status no Engine, nem o default ativo que exclui terminais)
 * para não divergir do próprio badge (`inboxCounts.tabs.all`, que sempre
 * conta tudo) — mesmo invariante "mesmos params ⇒ list e counts concordam"
 * que motivou o default ativo nas outras abas. Só entra quando o rail NÃO
 * escolheu um status explícito (Resolvida/Arquivada etc. seguem vencendo —
 * pedido explícito do usuário, não se sobrescreve).
 */
export function applyTabOverride(base: ConversationsFilters, tab: InboxTab): ConversationsFilters {
  const merged: ConversationsFilters = { ...base };
  if (tab === 'awaiting-me') {
    merged.assignedToUserId = 'me';
  } else if (tab === 'awaiting-anyone') {
    // 29/Mai/2026 — destrava conv órfã (assignedToUserId IS NULL).
    // Backend converte 'unassigned' → assignedToUserId: null no Prisma WHERE.
    merged.assignedToUserId = 'unassigned';
  } else if (tab === 'team-queue') {
    // Item 366 Fase 4 — fila do meu time: pool dos times do operador (resolvido
    // server-side via teamQueue=mine; não fixa assignedToUserId — é o pool).
    merged.teamQueue = 'mine';
  } else if (tab === 'ai-driving') {
    merged.operationMode = 'H_c';
  } else if (tab === 'all' && merged.status === undefined) {
    merged.status = 'any';
  }
  return merged;
}
